/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore - vue-shepherd ships without type declarations
import { useShepherd } from 'vue-shepherd';
import 'shepherd.js/dist/css/shepherd.css';

import type { LocationQueryRaw } from 'vue-router';

import { AdminRouteNames } from '@app/router/route-names';
import { trackEvent } from '@app/composables/posthog';

/**
 * First-time product tour for business owners.
 *
 * The tour is a sequence of centered modal steps. Before each step we navigate
 * to the relevant real page, so the owner sees the actual screen behind the
 * dimmed overlay. Because a brand-new business starts completely blank (empty
 * calendar, no customers), each step also embeds a small illustrative mock of
 * "what it looks like once you have data" — this is the main thing new owners
 * told us they couldn't picture. The mocks are static inline HTML, so they
 * never touch the live stores or the backend.
 */
export interface ProductTourContext {
  /** Whether the app is currently rendered in the mobile layout. */
  isMobile: () => boolean;
  /** Owner's first name, for a personal greeting. */
  getFirstName: () => string;
  /** Navigate to an admin page (router push + store page sync). */
  navigate: (page: AdminRouteNames, query?: LocationQueryRaw) => void;
  /** Open/close the mobile navigation drawer. */
  setMobileMenu: (open: boolean) => void;
  /** Called once the tour ends (completed or skipped) so the caller can persist it. */
  onFinish: (reason: 'completed' | 'skipped') => void;
}

const PURPLE = '#8864ee';
const INK = '#24104A';

const chip = (time: string, label: string, bg: string, border: string): string =>
  `<div style="display:flex;align-items:center;gap:8px;background:${bg};border-right:3px solid ${border};border-radius:8px;padding:7px 10px;font-size:12px;color:${INK}">
     <span style="font-weight:600">${time}</span><span style="opacity:.8">${label}</span>
   </div>`;

const optionRow = (icon: string, label: string): string =>
  `<div style="display:flex;align-items:center;gap:10px;padding:7px 0;border-bottom:1px solid rgba(0,0,0,.06);font-size:12px;color:${INK}">
     <i class="pi ${icon}" style="color:${PURPLE}"></i><span>${label}</span>
   </div>`;

const featureRow = (icon: string, title: string, desc: string): string =>
  `<div style="display:flex;align-items:flex-start;gap:10px;background:#f8f7fc;border-radius:10px;padding:9px">
     <i class="pi ${icon}" style="color:${PURPLE};margin-top:2px"></i>
     <div><div style="font-weight:600;color:${INK};font-size:12px">${title}</div>
     <div style="font-size:11px;opacity:.75">${desc}</div></div>
   </div>`;

const stat = (value: string, label: string): string =>
  `<div style="flex:1;background:#f8f7fc;border-radius:8px;padding:6px;text-align:center;justify-items: center; align-items: center;">
     <div style="font-weight:700;color:${INK};font-size:13px">${value}</div>
     <div style="font-size:10px;opacity:.7">${label}</div>
   </div>`;

const historyRow = (mark: string, date: string, label: string, status: string): string =>
  `<div style="display:flex;align-items:center;gap:8px;padding:5px 0;border-bottom:1px solid rgba(0,0,0,.05)">
     <span>${mark}</span><span style="direction:ltr">${date}</span>
     <span style="flex:1">${label}</span><span style="opacity:.7">${status}</span>
   </div>`;

const calendarMock = (): string => `
  <div>
    <p style="margin:0 0 8px">ביומן תראו את כל התורים והאירועים שלכם. לכל שירות יש צבע משלו, כדי שתזהו במבט אחד מה קורה ביום:</p>
    <div style="display:flex;flex-direction:column;gap:6px">
      ${chip('09:30', 'תספורת · דנה לוי', 'rgba(136,100,238,.12)', 'rgba(136,100,238,.6)')}
      ${chip('11:00', 'צבע ופן · מאיה כהן', 'rgba(219 234 254)', 'rgb(59 130 246)')}
      ${chip('14:00', 'הפסקת צהריים', 'repeating-linear-gradient(45deg,#f3f4f6,#f3f4f6 4px,#e5e7eb 4px,#e5e7eb 8px)', '#9ca3af')}
    </div>
    <p style="margin:8px 0 0;font-size:11px;opacity:.7">כחול = תור מוזמן · סגול = לקוח אישר הגעה · מקווקו = חסימת זמן ידנית שלכם.</p>
  </div>`;

const eventOptionsMock = (): string => `
  <div>
    <p style="margin:0 0 8px">לחיצה על תור פותחת תפריט פעולות מהיר. אלו האפשרויות שתקבלו:</p>
    <div style="background:#fff;border:1px solid rgba(0,0,0,.08);border-radius:12px;padding:6px 12px">
      ${optionRow('pi-pencil', 'עריכת פרטי התור ושינוי שעה')}
      ${optionRow('pi-credit-card', 'שליחת בקשת תשלום ללקוח')}
      ${optionRow('pi-whatsapp', 'שליחת הודעה או תזכורת בוואטסאפ')}
      ${optionRow('pi-check-circle', 'אישור התור או סימון ״הגיע / לא הגיע״')}
      ${optionRow('pi-user', 'מעבר לכרטיס הלקוח')}
      ${optionRow('pi-times-circle', 'ביטול התור')}
    </div>
  </div>`;

const schedulingMock = (): string => `
  <div>
    <p style="margin:0 0 10px">שתי דרכים להוסיף ליומן — לחצו על ⋯ בתחתית המסך, ולאחר מכן בחרו את האפשרות הרצויה:</p>
    <div style="display:flex;flex-direction:column;gap:8px">
      ${featureRow('pi-calendar-plus', 'זימון תור', 'קביעת תור ללקוח עם שירות, שעה ותזכורת אוטומטית.')}
      ${featureRow('pi-calendar-clock', 'הוספת אירוע', 'חסימת זמן אישי (פגישה, הפסקה) בלי לשייך לקוח.')}
    </div>
    <p style="margin:10px 0 0;font-size:11px;opacity:.7">טיפ: גם לחיצה ישירה על שעה פנויה ביומן פותחת קביעת תור.</p>
  </div>`;

const customerMock = (): string => `
  <div>
    <p style="margin:0 0 8px">בכל כרטיס לקוח מרוכז הכל. ככה הוא נראה כשהעסק כבר פעיל:</p>
    <div style="background:#fff;border:1px solid rgba(0,0,0,.08);border-radius:12px;padding:12px">
      <div style="display:flex;align-items:center;gap:10px">
        <div style="width:38px;height:38px;border-radius:50%;background:${PURPLE};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700">ד</div>
        <div style="flex:1">
          <div style="font-weight:700;color:${INK};font-size:13px">דנה לוי</div>
          <div style="font-size:11px;opacity:.7;direction:ltr;text-align:right">050-123-4567</div>
        </div>
        <span style="background:rgba(136,100,238,.12);color:${PURPLE};font-size:10px;font-weight:600;border-radius:999px;padding:3px 8px">לקוחה קבועה</span>
      </div>
      <div style="display:flex;gap:6px;margin:10px 0">
        ${stat('אישור טיפול', 'טפסים ואישורים')}
        ${stat('תור מתנה', 'הטבות חבר מועדון')}
        ${stat('הורדת חשבונית', 'היסטוריית תשלומים')}
      </div>
      <div style="font-size:11px;color:${INK}">
        ${historyRow('✓', '12.5', 'תספורת + פן', 'שולם')}
        ${historyRow('✓', '28.4', 'צבע', 'שולם')}
        ${historyRow('🕒', '20.6', 'תספורת', 'עתידי')}
      </div>
      <div style="margin-top:8px;background:#fff7ed;border:1px solid #fed7aa;border-radius:8px;padding:6px 8px;font-size:11px;color:#9a3412">📌 הערה: אלרגיה לאמוניה — להשתמש בצבע ללא אמוניה.</div>
    </div>
    <p style="margin:8px 0 0;font-size:11px;opacity:.7">היסטוריית תורים, תשלומים, הערות, קבצים ותוכנית נאמנות — הכל במקום אחד.</p>
  </div>`;

const automationsMock = (): string => `
  <div>
    <p style="margin:0 0 8px">כאן מגדירים הודעות שנשלחות ללקוחות אוטומטית — בלי שתצטרכו לזכור:</p>
    <div style="display:flex;flex-direction:column;gap:7px">
      ${featureRow('pi-bell', 'תזכורת לפני התור', 'SMS או וואטסאפ יום לפני — מוריד ביטולים ואי-הגעות.')}
      ${featureRow('pi-heart', '״מתגעגעים אליך״', 'פנייה אוטומטית ללקוח שלא הגיע הרבה זמן.')}
      ${featureRow('pi-star', 'בקשת ביקורת', 'הודעה אחרי התור עם בקשה לדרג אתכם.')}
    </div>
    <p style="margin:9px 0 0;font-size:11px;opacity:.7">נמצא בתפריט ״ניהול״ תחת ״אוטומציות״. מפעילים בלחיצה, וזה רץ לבד.</p>
  </div>`;

const settingsMock = (): string => `
  <div>
    <p style="margin:0 0 8px">לפני שמשתפים את העסק, כדאי להגדיר שני דברים בסיסיים:</p>
    <div style="display:flex;flex-direction:column;gap:7px">
      ${featureRow('pi-list', 'השירותים שלכם', 'שם, מחיר ומשך לכל שירות — כאן בעמוד ״שירותים״.')}
      ${featureRow('pi-clock', 'שעות הפעילות', 'נקבעות לכל עובד/ת בעמוד ״צוות״ — היומן יציע ללקוחות רק שעות אלו.')}
    </div>
    <p style="margin:9px 0 0;font-size:11px;opacity:.7">בעמוד ״העדפות״ אפשר גם לעצב את עמוד ההזמנות ולקבוע מדיניות ביטולים.</p>
  </div>`;

const shareMock = (): string => `
  <div>
    <p style="margin:0 0 8px">לעסק שלכם יש עמוד הזמנות אישי שלקוחות נכנסים אליו כדי לקבוע תור לבד — 24/7.</p>
    <div style="display:flex;align-items:center;gap:10px;background:rgba(136,100,238,.1);border-radius:10px;padding:10px">
      <i class="pi pi-share-alt" style="color:${PURPLE};font-size:18px"></i>
      <div style="font-size:12px;color:${INK}">לחצו על ⋯ בתחתית המסך, לאחר מכן בחרו <b>״אתר אישי״</b> כדי להעתיק את הקישור ולשתף בוואטסאפ, באינסטגרם או בכל מקום.</div>
    </div>
    <p style="margin:9px 0 0;font-size:11px;opacity:.7">כל תור שייקבע דרך הקישור יופיע ישירות ביומן שלכם.</p>
  </div>`;

const finishMock = (): string => `
  <div>
    <p style="margin:0 0 8px">זהו! עכשיו אתם מכירים את הכלים החשובים. הצעדים הראשונים המומלצים:</p>
    <ol style="margin:0;padding-inline-start:18px;font-size:12px;color:${INK};line-height:1.7">
      <li>הגדירו שירותים ושעות פעילות</li>
      <li>הוסיפו כמה לקוחות או ייבאו מאנשי הקשר</li>
      <li>שתפו את עמוד ההזמנות</li>
    </ol>
  </div>`;

// A small "you are here" chip so the owner always knows which page a step is about.
const pageChip = (label: string): string =>
  `<div style="display:inline-flex;align-items:center;gap:6px;background:rgba(136,100,238,.12);color:#6b3fc4;font-size:11px;font-weight:600;border-radius:999px;padding:3px 10px;margin-bottom:10px">
     <i class="pi pi-map-marker" style="font-size:11px"></i><span>${label}</span>
   </div>`;

export function useProductTour(ctx: ProductTourContext) {
  const tour = useShepherd({
    useModalOverlay: true,
    defaultStepOptions: {
      classes: 'product-tour',
      cancelIcon: { enabled: true },
      scrollTo: { behavior: 'smooth', block: 'center' },
    },
  });

  tour.on('complete', () => {
    trackEvent('product_tour_completed');
    ctx.onFinish('completed');
  });
  tour.on('cancel', () => {
    const current = tour.getCurrentStep?.();
    trackEvent('product_tour_skipped', { step: current?.id });
    ctx.onFinish('skipped');
  });

  // Navigate to the page a step belongs to, then resolve once it has had a
  // moment to render. Runs on both forward (next) and backward (back) shows.
  // When the page actually changes we wait noticeably longer so the owner sees
  // where they landed *before* the next explanation card appears; staying on the
  // same page only needs a short beat.
  let currentTourPage: AdminRouteNames | undefined;
  const goTo = (page: AdminRouteNames, args?: { query?: LocationQueryRaw }): Promise<void> =>
    new Promise((resolve) => {
      if (ctx.isMobile()) ctx.setMobileMenu(false);
      const changed = currentTourPage !== page;
      currentTourPage = page;
      ctx.navigate(page, args?.query);
      setTimeout(resolve, changed ? 850 : 150);
    });

  const nextButton = () => ({ text: 'הבא', action: () => tour.next() });
  const backButton = () => ({ text: 'הקודם', action: () => tour.back(), secondary: true });

  const addStep = (options: Record<string, unknown>) => {
    const id = options.id as string;
    tour.addStep({
      ...options,
      when: {
        show() {
          trackEvent('product_tour_step_viewed', { step: id });
        },
      },
    });
  };

  const buildSteps = () => {
    // Rebuild from scratch so re-running the tour never duplicates steps.
    for (const step of [...tour.steps]) tour.removeStep(step.id);

    const name = ctx.getFirstName();

    addStep({
      id: 'welcome',
      title: `ברוכים הבאים${name ? `, ${name}` : ''}! 👋`,
      text: `<div><p style="margin:0 0 6px">תכף נצא לסיור קצר של כ-2 דקות שיראה לכם איך הכל עובד — היומן, קביעת תורים, לקוחות ותזכורות אוטומטיות.</p><p style="margin:0;opacity:.75;font-size:11px">אפשר לעצור בכל רגע בלחיצה על ה-✕.</p></div>`,
      beforeShowPromise: () => goTo(AdminRouteNames.Home),
      buttons: [
        { text: 'אדלג', action: () => tour.cancel(), secondary: true },
        { text: 'יאללה, מתחילים', action: () => tour.next() },
      ],
    });

    addStep({
      id: 'calendar',
      title: 'היומן שלכם',
      text: pageChip('דף הבית · יומן') + calendarMock(),
      beforeShowPromise: () => goTo(AdminRouteNames.Home),
      buttons: [backButton(), nextButton()],
    });

    addStep({
      id: 'event-options',
      title: 'לחיצה על תור',
      text: pageChip('דף הבית · יומן') + eventOptionsMock(),
      buttons: [backButton(), nextButton()],
    });

    addStep({
      id: 'scheduling',
      title: 'קביעת תור והוספת אירוע',
      text: pageChip('דף הבית · יומן') + schedulingMock(),
      beforeShowPromise: () => goTo(AdminRouteNames.Home),
      buttons: [backButton(), nextButton()],
    });

    addStep({
      id: 'customers',
      title: 'כרטיס הלקוח',
      text: pageChip('לקוחות ומשובים') + customerMock(),
      beforeShowPromise: () => goTo(AdminRouteNames.Clients),
      buttons: [backButton(), nextButton()],
    });

    addStep({
      id: 'automations',
      title: 'תזכורות ואוטומציות',
      text: pageChip('ניהול · אוטומציות') + automationsMock(),
      beforeShowPromise: () => goTo(AdminRouteNames.Communication, { query: { tab: 0 } }),
      buttons: [backButton(), nextButton()],
    });

    addStep({
      id: 'settings',
      title: 'הגדרת העסק',
      text: pageChip('שירותים') + settingsMock(),
      beforeShowPromise: () => goTo(AdminRouteNames.BusinessConfigurations),
      buttons: [backButton(), nextButton()],
    });

    addStep({
      id: 'share',
      title: 'שיתוף עמוד ההזמנות',
      text: pageChip('דף הבית') + shareMock(),
      beforeShowPromise: () => goTo(AdminRouteNames.Home),
      buttons: [backButton(), nextButton()],
    });

    addStep({
      id: 'finish',
      title: 'מוכנים לצאת לדרך! 🚀',
      text: finishMock(),
      beforeShowPromise: () => goTo(AdminRouteNames.Home),
      buttons: [{ text: 'סיום', action: () => tour.complete() }],
    });
  };

  const startTour = () => {
    trackEvent('product_tour_started', { is_mobile: ctx.isMobile() });
    buildSteps();
    tour.start();
  };

  return { startTour, tour };
}
